function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import * as React from 'react';
import { useState } from 'react';
import { InstanceProps } from 'react-modal-promise';
import { Cohort } from 'common/query-builder/cohorts-query-builder/models/cohort';
import { ReportName } from 'common/report/constants';
import { getProjectId, getReportUrl, getWorkspaceId } from 'common/report/util';
import { encodeRison } from 'common/report/util/url';
import { useTranslation } from 'common/i18n/hooks';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { ModalReact, ModalType } from 'common/components/modal';
import { pluralize, truncate } from 'common/util/string';
import { useReportLayoutManager } from 'common/contexts/react/report-layout-manager-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { isGlobalSidenavEnabled } from 'iron/routes/global-sidenav/utils';
import { CohortDetail } from '../create-cohort-button';
import { ProfileSidebarReact } from '../profile-sidebar';
import { TrackingEvent } from './tracking';
import { SidebarUserListReact } from './sidebar-user-list';
import { SegmentLabelCreator } from './sidebar-user-list/types';
import styles from './index.module.styl';
enum TabOptions {
  USER_LIST = "USER_LIST",
  USER_DETAIL = "USER_DETAIL",
}
type Props = {
  hideCohortFunctionality?: boolean;
  includeAllUsers: boolean;
  segments: string[];
  reportName: ReportName;
  color?: string;
  dataGroupName?: string;
  fromDate?: string;
  toDate?: string;
  cohort: Cohort;
  cohortDetails: CohortDetail[];
  createSegmentLabel: SegmentLabelCreator;
} & InstanceProps<void>;
export function UsersSidebar(_ref: Props) {
  let {
    isOpen,
    onReject: onClose,
    color = "var(--component-outline-color)",
    dataGroupName = "user",
    cohort,
    cohortDetails,
    createSegmentLabel,
    hideCohortFunctionality,
    includeAllUsers,
    fromDate,
    toDate,
    reportName,
    segments
  }: Props = _ref;
  // Serves as a state for controlling actual widget to be rendered in the sidebar
  const [selectedTab, setSelectedTab] = useState(TabOptions.USER_LIST);
  const [distinctID, setDistinctID] = useState("");
  const {
    t
  } = useTranslation();
  const reportLayoutManager = useReportLayoutManager();
  const currentProject = useCurrentProject();
  const handleUserSelected = (ev: CustomEvent<{
    distinctID: string;
  }>) => {
    mixpanel.track(TrackingEvent.ViewUserDetail, {
      'User ID': ev.detail.distinctID
    });
    setDistinctID(ev.detail.distinctID);
    setSelectedTab(TabOptions.USER_DETAIL);
  };
  const handleBack = () => {
    setDistinctID("");
    setSelectedTab(TabOptions.USER_LIST);
  };
  const handleClose = () => {
    onClose();
  };
  const handleTabChange = (ev: CustomEvent<{
    selectedTab: string;
  }>) => {
    mixpanel.track(TrackingEvent.ProfileTabChange, {
      'Tab Name': ev.detail.selectedTab
    });
  };
  const getUserProfilePageUrl = () => {
    let url = "".concat(getReportUrl("profile", {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId()
    }), "#distinct_id=").concat(encodeURIComponent(distinctID));
    if (cohort.dataGroupId) {
      url = "".concat(url, "&data_group_id=").concat(cohort.dataGroupId);
    }
    return url;
  };
  const getUsersPageUrl = () => {
    const params = _objectSpread({
      include_all_users: includeAllUsers,
      serialized_cohort: encodeURIComponent(JSON.stringify(cohort.serialize())),
      query_source: "View Users"
    }, cohort.dataGroupId ? {
      data_group_id: cohort.dataGroupId
    } : {});
    const url = getReportUrl(ReportName.Users, {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId()
    }) + "#list/".concat(encodeRison(params));
    return url;
  };
  const trackExpandUsersPage = () => {
    mixpanel.track(TrackingEvent.ExpandUsers);
  };
  const trackExpandProfilePage = () => {
    mixpanel.track(TrackingEvent.ExpandProfile, {
      'Tab Name': selectedTab
    });
  };
  const userSidebarWidth = reportLayoutManager.getUserSidebarWidth();
  const title = t("view-users.title", {
    context: dataGroupName === "user" ? "user" : undefined,
    group: truncate(pluralize(dataGroupName, true), 25)
  });
  return <ModalReact tabIndex={-1} closeable hideBackdrop hideCloseButton modalType={ModalType.RightDrawer} open={isOpen} onStackableClosed={handleClose} globalSidenavEnabled={isGlobalSidenavEnabled(currentProject.get().value)} data-sentry-element="ModalReact" data-sentry-component="UsersSidebar" data-sentry-source-file="index.tsx">
      <SidebarUserListReact cohort={cohort} cohortDetails={cohortDetails} color={color} createSegmentLabel={createSegmentLabel} dataGroupName={dataGroupName} hideCohortFunctionality={hideCohortFunctionality} includeAllUsers={includeAllUsers} reportName={reportName} segments={segments}
    // use display none so that the list wont reload when user clicks go back from user details view
    style={{
      display: selectedTab === TabOptions.USER_LIST ? "block" : "none",
      width: "".concat(userSidebarWidth, "px"),
      overflow: "hidden"
    }} onUserSelected={handleUserSelected} data-sentry-element="SidebarUserListReact" data-sentry-source-file="index.tsx">
        {!hideCohortFunctionality && <ButtonReact className={styles.openInNewPage} href={getUsersPageUrl()} icon={"expand"} size={ButtonSize.Small} target={"_blank"} theme={ButtonTheme.Tertiary} notFocusable onClick={() => trackExpandUsersPage()}>
            {t("view-users.expand")}
          </ButtonReact>}
        <ButtonReact className={styles.close} icon={"x"} theme={ButtonTheme.Tertiary} onClick={handleClose} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx" />
      </SidebarUserListReact>
      {selectedTab === TabOptions.USER_DETAIL && <ProfileSidebarReact forRightDrawer distinctId={distinctID} width={userSidebarWidth} onTabChange={handleTabChange} {...cohort.dataGroupId ? {
      dataGroupId: cohort.dataGroupId
    } : {}} {...fromDate && toDate ? {
      fromDate,
      toDate
    } : {}}>
          <ButtonReact className={styles.goBackButton} icon={"arrow-left"} theme={ButtonTheme.Tertiary} tooltip={(dataGroupName === null || dataGroupName === void 0 ? void 0 : dataGroupName.length) > 25 ? {
        text: title
      } : undefined} onClick={handleBack}>
            {title}
          </ButtonReact>
          <ButtonReact className={styles.openInNewPage} href={getUserProfilePageUrl()} icon={"expand"} size={ButtonSize.Small} slot='header-actions' target={"_blank"} theme={ButtonTheme.Tertiary} onClick={() => trackExpandProfilePage()}>
            {t("view-users.expand")}
          </ButtonReact>
          <ButtonReact className={styles.close} icon={"x"} theme={ButtonTheme.Tertiary} onClick={handleClose} />
        </ProfileSidebarReact>}
    </ModalReact>;
}